import { getReferralsApiUrl } from '../config/api';
import { FormActionFramework } from './engine';
const bindings = new Map();
const inflight = new Map();
export function getBoundEngine(formId) {
    return bindings.get(String(formId))?.engine ?? null;
}
export function resetFafBindings(formId) {
    if (formId != null && String(formId).trim() !== '') {
        detachBinding(String(formId));
        inflight.delete(String(formId));
        return;
    }
    for (const key of [...bindings.keys()]) {
        detachBinding(key);
    }
    inflight.clear();
}
export async function fetchFafConfig(formID) {
    const response = await fetch(getReferralsApiUrl('/faf/config'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formID }),
    });
    const text = await response.text();
    let payload;
    try {
        payload = JSON.parse(text);
    }
    catch {
        throw new Error(`FAF config returned non-JSON (${response.status}): ${text.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `FAF config failed (${response.status})`);
    }
    return payload.config ?? null;
}
/**
 * Load FAF config for a form, init the engine, run formLoad, and attach formChange.
 * Cached per formId + survey instance. Errors are logged; never throws to the caller.
 */
export async function bindSurvey(options) {
    const numericFormId = toNumericFormId(options.formId);
    if (!numericFormId || !options.survey) {
        return null;
    }
    const formIdKey = String(options.bindingKey ?? options.formId);
    const existing = bindings.get(formIdKey);
    if (existing && existing.survey === options.survey) {
        return existing.engine;
    }
    const pending = inflight.get(formIdKey);
    if (pending) {
        const engine = await pending;
        const after = bindings.get(formIdKey);
        if (after && after.survey === options.survey) {
            return engine;
        }
    }
    const run = (async () => {
        try {
            if (existing) {
                detachBinding(formIdKey);
            }
            const config = await fetchFafConfig(numericFormId);
            if (!configHasSteps(config)) {
                return null;
            }
            const engine = new FormActionFramework();
            engine.init(config, {
                formId: numericFormId,
                pageUrl: typeof window !== 'undefined' ? window.location.href : null,
                ...(options.host ?? {}),
            });
            await executeFormLoadPreservingData(engine, options.survey);
            const binding = {
                engine,
                survey: options.survey,
                executing: false,
                onValueChanged: () => undefined,
            };
            binding.onValueChanged = (sender, changeOptions) => {
                if (binding.executing)
                    return;
                binding.executing = true;
                void executeMilestone(engine, sender, 'formChange', {
                    name: changeOptions?.name,
                    value: changeOptions?.value,
                })
                    .catch((err) => {
                    console.error('[FAF] formChange failed', err);
                })
                    .finally(() => {
                    binding.executing = false;
                });
            };
            options.survey.onValueChanged.add(binding.onValueChanged);
            bindings.set(formIdKey, binding);
            return engine;
        }
        catch (err) {
            console.error('[FAF] bindSurvey failed', {
                formId: numericFormId,
                error: err instanceof Error ? err.message : String(err),
            });
            return null;
        }
    })().finally(() => {
        inflight.delete(formIdKey);
    });
    inflight.set(formIdKey, run);
    return run;
}
export async function executeMilestone(engine, survey, milestone, change) {
    const formValues = survey?.data && typeof survey.data === 'object'
        ? { ...survey.data }
        : {};
    const context = engine.createContext({
        form: survey,
        formValues,
        change,
    });
    return engine.execute(context, milestone);
}
export async function executeValidateThenSubmit(formId, survey) {
    if (formId == null || !survey)
        return null;
    const engine = getBoundEngine(formId);
    if (!engine)
        return null;
    const validateCtx = await executeMilestone(engine, survey, 'formValidate');
    if (validateCtx.shouldStop)
        return validateCtx;
    return executeMilestone(engine, survey, 'formSubmit');
}
export async function executePostSubmit(formId, survey) {
    if (formId == null || !survey)
        return null;
    const engine = getBoundEngine(formId);
    if (!engine)
        return null;
    return executeMilestone(engine, survey, 'formPostSubmit');
}
async function executeFormLoadPreservingData(engine, survey) {
    const snapshot = snapshotNonEmptyFormData(survey);
    await executeMilestone(engine, survey, 'formLoad');
    restoreFormDataSnapshot(survey, snapshot);
}
function configHasSteps(config) {
    if (!config?.milestones)
        return false;
    return Object.values(config.milestones).some((list) => Array.isArray(list) && list.length > 0);
}
function toNumericFormId(formId) {
    const n = typeof formId === 'number' ? formId : Number(String(formId).trim());
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return n;
}
function detachBinding(formIdKey) {
    const binding = bindings.get(formIdKey);
    if (!binding)
        return;
    try {
        binding.survey.onValueChanged.remove(binding.onValueChanged);
    }
    catch {
        // Survey instance may already be gone.
    }
    bindings.delete(formIdKey);
}
function isEmptyFormValue(value) {
    return value === undefined || value === null || value === '';
}
function snapshotNonEmptyFormData(survey) {
    const data = survey?.data && typeof survey.data === 'object'
        ? survey.data
        : {};
    const snapshot = {};
    for (const [key, value] of Object.entries(data)) {
        if (!isEmptyFormValue(value)) {
            snapshot[key] = value;
        }
    }
    return snapshot;
}
function readSurveyFieldValue(survey, fieldName) {
    if (!survey || !fieldName)
        return undefined;
    let value;
    if (typeof survey.getQuestionByName === 'function') {
        const question = survey.getQuestionByName(fieldName);
        if (question && 'value' in question) {
            value = question.value;
        }
    }
    if ((value === undefined || value === null || value === '') &&
        survey.data &&
        typeof survey.data === 'object' &&
        fieldName in survey.data) {
        value = survey.data[fieldName];
    }
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    return value;
}
function restoreFormDataSnapshot(survey, snapshot) {
    if (!survey || !snapshot)
        return;
    for (const [fieldName, value] of Object.entries(snapshot)) {
        const current = readSurveyFieldValue(survey, fieldName);
        if (current !== undefined)
            continue;
        if (typeof survey.setValue === 'function') {
            survey.setValue(fieldName, value);
        }
    }
}
